<script setup lang="ts">

import { AppText } from '@shared/ui/typography'
import { BtnPrimary } from '@shared/ui/buttons'
import { BtnSecondary } from '@shared/ui/buttons'
import { CardAdmin } from '@shared/ui/cards'

import { useOrderInvoice } from '../../model/invoice/order-invoice.hook'
import OrderInvoiceFields from './OrderInvoiceFields.vue'
import OrderInvoiceFiles from './OrderInvoiceFiles.vue'
import OrderInvoicePreview from './OrderInvoicePreview.vue'

defineOptions({
  name: 'OrderInvoicePage',
})

const props = defineProps<{
  orderId: number
}>()

const {
  order,
  draft,
  invoices,
  isPaid,
  isPreviewLoading,
  canManage,
  canSave,
  canClear,
  setParentFullName,
  setAddress,
  setItemDetails,
  clearFields,
  previewInvoice,
  saveInvoice,
  downloadInvoice,
} = useOrderInvoice({ orderId: computed(() => props.orderId) })

const isUnpaid = computed<boolean>(() => !!order.value && !isPaid.value)
</script>

<template>
  <CardAdmin
    class="order-invoice-page"
    fullwidth
    flexible
  >
    <template #title>
      <div class="order-invoice-page__title">
        <AppText name="h3">
          Customise invoice
        </AppText>
        <AppText
          name="body1-medium"
          color="slate-500"
        >
          #{{ orderId }}
        </AppText>
      </div>
    </template>
    <template
      v-if="canManage && !isUnpaid"
      #header
    >
      <div class="order-invoice-page__actions">
        <BtnSecondary
          text="Clear"
          :disabled="!canClear"
          @click="clearFields"
        />
        <BtnPrimary
          text="Save"
          color="emerald"
          :disabled="!canSave"
          @click="saveInvoice"
        />
      </div>
    </template>
    <div
      v-if="isUnpaid"
      class="order-invoice-page__unpaid"
    >
      <AppText
        name="body1"
        color="slate-500"
      >
        Invoice customisation is available only for paid orders
      </AppText>
    </div>
    <div
      v-else
      class="order-invoice-page__layout"
    >
      <div class="order-invoice-page__sidebar">
        <OrderInvoiceFiles
          :invoices="invoices"
          :disabled="isPreviewLoading"
          @click:download="downloadInvoice"
        />
        <OrderInvoiceFields
          v-if="draft"
          :fields="draft"
          :readonly="!canManage"
          :can-preview="canManage"
          :preview-disabled="isPreviewLoading"
          @change:parent-full-name="setParentFullName"
          @change:address="setAddress"
          @change:item-details="setItemDetails"
          @click:preview="previewInvoice"
        />
      </div>
      <OrderInvoicePreview class="order-invoice-page__preview" />
    </div>
  </CardAdmin>
</template>

<style scoped lang="scss" src="./OrderInvoicePage.scss" />
